
import { supabase } from "@/integrations/supabase/client";
import { getConfig, isStaging } from "@/config/environment";

interface CheckPaymentStatusParams {
  paymentId?: string;
  transactionRef?: string;
  courseId: string;
  userId: string;
}

interface PaymentStatusResponse {
  success: boolean;
  status: "completed" | "pending" | "failed" | "not_found"; 
  message?: string;
  transactionId?: string;
  orderId?: string;
}

export const checkPaymentStatus = async (params: CheckPaymentStatusParams): Promise<PaymentStatusResponse> => {
  try {
    const { paymentId, transactionRef, courseId, userId } = params;
    const config = getConfig();
    
    if ((!paymentId && !transactionRef) || !courseId || !userId) {
      throw new Error("Missing required parameters");
    }
    
    // Log environment info if in debug mode
    if (config.debugMode) {
      console.log(`Checking payment status in ${import.meta.env.MODE} environment`);
      if (isStaging()) {
        console.log("STAGING ENVIRONMENT: Payments are in test mode");
      }
    }
    
    // First check if we already have a payment record in our database
    const { data: existingPayment, error: checkError } = await supabase
      .from('course_payments')
      .select('id, payment_id')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('status', 'completed')
      .maybeSingle();
      
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking payment:', checkError);
      throw checkError;
    }
    
    if (existingPayment) {
      return {
        success: true,
        status: "completed",
        message: "Payment already completed",
        transactionId: existingPayment.payment_id
      };
    }
    
    // If no completed payment found, call the check-payment-status function
    const response = await supabase.functions.invoke('check-payment-status', {
      body: {
        orderId: paymentId,
        transactionRef,
        courseId,
        userId,
        environment: import.meta.env.MODE // Pass the current environment to the edge function
      }
    });
    
    if (response.error) {
      throw new Error(response.error.message || "Failed to check payment status");
    }
    
    return response.data;
  } catch (error) {
    console.error("Error checking payment status:", error);
    return {
      success: false,
      status: "failed",
      message: error instanceof Error ? error.message : "An unknown error occurred"
    };
  }
};
